import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  X,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Download,
  Trash2,
  Loader2
} from 'lucide-react';
import Papa from 'papaparse';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface ImportPatientsCSVProps {
  onClose: () => void;
  onSuccess: (importedCount: number) => void;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'results';

interface ParsedRow {
  rowNumber: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phone: string;
  email: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  valid: boolean;
  errors: string[];
}

interface ImportResult {
  imported: number;
  skipped: number;
  errors: Array<{ row: number; reason: string }>;
  duplicates: Array<{ row: number; reason: string }>;
}

// Map CSV column headers (case-insensitive) to our expected fields
const HEADER_MAP: Record<string, string> = {
  'firstname': 'firstName',
  'first_name': 'firstName',
  'first name': 'firstName',
  'lastname': 'lastName',
  'last_name': 'lastName',
  'last name': 'lastName',
  'dateofbirth': 'dateOfBirth',
  'date_of_birth': 'dateOfBirth',
  'date of birth': 'dateOfBirth',
  'dob': 'dateOfBirth',
  'birthday': 'dateOfBirth',
  'phone': 'phone',
  'phone_number': 'phone',
  'phone number': 'phone',
  'phonenumber': 'phone',
  'email': 'email',
  'email_address': 'email',
  'email address': 'email',
  'street': 'street',
  'address': 'street',
  'street_address': 'street',
  'street address': 'street',
  'street2': 'street2',
  'address2': 'street2',
  'address_2': 'street2',
  'address 2': 'street2',
  'street_2': 'street2',
  'apt': 'street2',
  'suite': 'street2',
  'unit': 'street2',
  'apt/suite': 'street2',
  'address line 2': 'street2',
  'addressline2': 'street2',
  'city': 'city',
  'state': 'state',
  'zipcode': 'zipCode',
  'zip_code': 'zipCode',
  'zip code': 'zipCode',
  'zip': 'zipCode',
  'postal_code': 'zipCode',
  'postal code': 'zipCode',
};

const REQUIRED_FIELDS = ['firstName', 'lastName', 'dateOfBirth', 'phone', 'email'];
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ImportPatientsCSV: React.FC<ImportPatientsCSVProps> = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState<ImportStep>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const normalizeHeader = (header: string): string => {
    const normalized = header.trim().toLowerCase();
    return HEADER_MAP[normalized] || normalized;
  };

  const validateRow = (row: any, rowNumber: number): ParsedRow => {
    const errors: string[] = [];

    const mapped: ParsedRow = {
      rowNumber,
      firstName: (row.firstName || '').trim(),
      lastName: (row.lastName || '').trim(),
      dateOfBirth: (row.dateOfBirth || '').trim(),
      phone: (row.phone || '').trim(),
      email: (row.email || '').trim(),
      street: (row.street || '').trim(),
      street2: (row.street2 || '').trim(),
      city: (row.city || '').trim(),
      state: (row.state || '').trim(),
      zipCode: (row.zipCode || '').trim(),
      valid: true,
      errors: []
    };

    // Check required fields
    if (!mapped.firstName) errors.push('Missing first name');
    if (!mapped.lastName) errors.push('Missing last name');
    if (!mapped.dateOfBirth) errors.push('Missing date of birth');
    if (!mapped.phone) errors.push('Missing phone');
    if (!mapped.email) errors.push('Missing email');

    // Validate email format
    if (mapped.email && !emailRegex.test(mapped.email)) {
      errors.push('Invalid email format');
    }

    // Validate date
    if (mapped.dateOfBirth) {
      const dob = new Date(mapped.dateOfBirth);
      if (isNaN(dob.getTime())) {
        errors.push('Invalid date format');
      }
    }

    mapped.errors = errors;
    mapped.valid = errors.length === 0;
    return mapped;
  };

  const parseCSV = useCallback((file: File) => {
    setFileName(file.name);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: normalizeHeader,
      complete: (results) => {
        if (results.data.length === 0) {
          toast.error('CSV file is empty or has no data rows');
          return;
        }

        if (results.data.length > 500) {
          toast.error('Maximum 500 patients per import. Please split your file.');
          return;
        }

        // Check if required headers are present
        const headers = results.meta.fields || [];
        const missingHeaders = REQUIRED_FIELDS.filter(f => !headers.includes(f));
        if (missingHeaders.length > 0) {
          toast.error(`Missing required columns: ${missingHeaders.join(', ')}. Check your CSV headers.`);
          return;
        }

        const rows = results.data.map((row: any, index: number) =>
          validateRow(row, index + 1)
        );

        setParsedRows(rows);
        setStep('preview');
      },
      error: (error) => {
        toast.error(`Failed to parse CSV: ${error.message}`);
      }
    });
  }, []);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      parseCSV(file);
    } else {
      toast.error('Please upload a CSV file');
    }
  }, [parseCSV]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseCSV(file);
    }
  }, [parseCSV]);

  const removeRow = (rowNumber: number) => {
    setParsedRows(prev => prev.filter(r => r.rowNumber !== rowNumber));
  };

  const downloadTemplate = () => {
    const headers = 'firstName,lastName,dateOfBirth,phone,email,street,street2,city,state,zipCode';
    const example = 'John,Doe,1985-03-15,555-123-4567,john.doe@email.com,123 Main St,Apt 4B,Springfield,IL,62701';
    const csv = `${headers}\n${example}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'patient_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    const validRows = parsedRows.filter(r => r.valid);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setStep('importing');

    try {
      const patients = validRows.map(row => ({
        firstName: row.firstName,
        lastName: row.lastName,
        dateOfBirth: row.dateOfBirth,
        phone: row.phone,
        email: row.email,
        address: (row.street || row.street2 || row.city || row.state || row.zipCode) ? {
          street: row.street,
          street2: row.street2 || undefined,
          city: row.city,
          state: row.state,
          zipCode: row.zipCode
        } : undefined
      }));

      const response = await apiClient.bulkImportPatients(patients);

      if (response.success && response.data) {
        setImportResult(response.data);
        setStep('results');
        if (response.data.imported > 0) {
          onSuccess(response.data.imported);
        }
      } else {
        toast.error(response.error || 'Import failed');
        setStep('preview');
      }
    } catch (error) {
      toast.error('Import failed. Please try again.');
      setStep('preview');
    }
  };

  const validCount = parsedRows.filter(r => r.valid).length;
  const invalidCount = parsedRows.filter(r => !r.valid).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Import Patients from CSV</h2>
            <p className="text-sm text-gray-500 mt-1">
              {step === 'upload' && 'Upload a CSV file with patient data'}
              {step === 'preview' && `${fileName} — ${validCount} valid, ${invalidCount} invalid`}
              {step === 'importing' && 'Importing patients...'}
              {step === 'results' && 'Import complete'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Upload Step */}
          {step === 'upload' && (
            <div className="space-y-6">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                  dragOver
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <Upload className={`h-12 w-12 mx-auto mb-4 ${dragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className="text-lg font-medium text-gray-700">
                  Drop your CSV file here
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  or click to browse files
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileInput}
                  className="hidden"
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Required columns:</h4>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['firstName', 'lastName', 'dateOfBirth', 'phone', 'email'].map(col => (
                    <span key={col} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                      {col}
                    </span>
                  ))}
                </div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Optional columns:</h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['street', 'street2', 'city', 'state', 'zipCode'].map(col => (
                    <span key={col} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full font-medium">
                      {col}
                    </span>
                  ))}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); downloadTemplate(); }}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download template CSV
                </button>
              </div>
            </div>
          )}

          {/* Preview Step */}
          {step === 'preview' && (
            <div className="space-y-4">
              {/* Summary stats */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">{validCount} valid</span>
                </div>
                {invalidCount > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-red-700">{invalidCount} invalid</span>
                  </div>
                )}
                <span className="text-sm text-gray-500">{parsedRows.length} total rows</span>
              </div>

              {/* Preview table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">First Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Last Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">DOB</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Phone</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">City</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">State</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {parsedRows.map(row => (
                        <tr
                          key={row.rowNumber}
                          className={row.valid ? 'bg-white' : 'bg-red-50'}
                        >
                          <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                          <td className="px-3 py-2">
                            {row.valid ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <div className="group relative">
                                <AlertCircle className="h-4 w-4 text-red-500" />
                                <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                  {row.errors.join(', ')}
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 font-medium">{row.firstName || <span className="text-red-400 italic">missing</span>}</td>
                          <td className="px-3 py-2 font-medium">{row.lastName || <span className="text-red-400 italic">missing</span>}</td>
                          <td className="px-3 py-2">{row.dateOfBirth || <span className="text-red-400 italic">missing</span>}</td>
                          <td className="px-3 py-2">{row.phone || <span className="text-red-400 italic">missing</span>}</td>
                          <td className="px-3 py-2 truncate max-w-[200px]">{row.email || <span className="text-red-400 italic">missing</span>}</td>
                          <td className="px-3 py-2 text-gray-500">{row.city || '—'}</td>
                          <td className="px-3 py-2 text-gray-500">{row.state || '—'}</td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => removeRow(row.rowNumber)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              title="Remove row"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {invalidCount > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    {invalidCount} row{invalidCount !== 1 ? 's' : ''} will be skipped due to validation errors.
                    Only the {validCount} valid row{validCount !== 1 ? 's' : ''} will be imported.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Importing Step */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
              <p className="text-lg font-medium text-gray-700">Importing {validCount} patients...</p>
              <p className="text-sm text-gray-500 mt-1">This may take a moment</p>
            </div>
          )}

          {/* Results Step */}
          {step === 'results' && importResult && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-700">{importResult.imported}</div>
                  <div className="text-sm text-green-600">Imported</div>
                </div>
                {importResult.duplicates.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                    <AlertTriangle className="h-8 w-8 text-amber-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-amber-700">{importResult.duplicates.length}</div>
                    <div className="text-sm text-amber-600">Duplicates Skipped</div>
                  </div>
                )}
                {importResult.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-red-700">{importResult.errors.length}</div>
                    <div className="text-sm text-red-600">Errors</div>
                  </div>
                )}
              </div>

              {/* Duplicate details */}
              {importResult.duplicates.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Duplicates skipped:</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-40 overflow-y-auto">
                      {importResult.duplicates.map((dup, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 text-sm border-b last:border-0 bg-amber-50">
                          <span className="text-gray-500 w-12">Row {dup.row}</span>
                          <span className="text-amber-700">{dup.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Error details */}
              {importResult.errors.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Errors:</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-40 overflow-y-auto">
                      {importResult.errors.map((err, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 text-sm border-b last:border-0 bg-red-50">
                          <span className="text-gray-500 w-12">Row {err.row}</span>
                          <span className="text-red-700">{err.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6 flex items-center justify-between">
          {step === 'upload' && (
            <div className="flex justify-end w-full">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={() => { setStep('upload'); setParsedRows([]); setFileName(''); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={validCount === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                Import {validCount} Patient{validCount !== 1 ? 's' : ''}
              </button>
            </>
          )}

          {step === 'results' && (
            <div className="flex justify-end w-full">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
